import React, { useState } from "react";
import styled from "styled-components";
import { useDraggable } from "@dnd-kit/core";
import { colors } from "utils";

const PlainCode = styled.span`
  color: ${colors.nightlyWoods950};
  display: inline-flex;
  font-size: 14px;
  line-height: 20px;
  padding: 8px;
  text-transform: uppercase;
`;

const RailPanel = styled.aside`
  background-color: ${colors.milkGlass200};
  border-left: 1px solid ${colors.blue300};
  bottom: 0;
  box-sizing: border-box;
  overflow-y: auto;
  padding: 32px;
  position: fixed;
  right: 0;
  top: 0;
  width: 312px;
  z-index: 40;
`;

const RailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RailHeading = styled.h2`
  color: ${colors.nightlyWoods500};
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.25px;
  line-height: 26px;
  margin: 0;
`;

const GripIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="9" cy="5" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="9" cy="19" r="1.5" />
    <circle cx="15" cy="5" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="15" cy="19" r="1.5" />
  </svg>
);

// Boxed chip with the drag-grip affordance. Used for every rail item and as the
// drag overlay so each unassigned code reads as draggable.
export const UnassignedChip = ({ code, dragging }) => (
  <div style={{
    alignItems: "center",
    backgroundColor: colors.blue50,
    borderRadius: 8,
    boxShadow: dragging ? "0px 4px 12px rgba(14, 26, 49, 0.18)" : "none",
    color: colors.nightlyWoods950,
    display: "inline-flex",
    gap: 8,
    padding: 8,
  }}>
    <span style={{ color: colors.nightlyWoods500, display: "flex" }}><GripIcon /></span>
    <span style={{ fontSize: 14, lineHeight: "20px", textTransform: "uppercase" }}>{code}</span>
  </div>
);

const DraggableCode = ({ code }) => {
  const [active, setActive] = useState(false);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `unassigned:${code}`,
    data: { kind: "unassigned", code },
  });
  // Plain text at rest; the grip + box drag affordance appears on hover/focus.
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      aria-label={`Group code ${code}, draggable. Assign to a standard.`}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
      style={{ cursor: "grab", opacity: isDragging ? 0.4 : 1, touchAction: "none", width: "fit-content" }}
    >
      {active || isDragging ? <UnassignedChip code={code} /> : <PlainCode>{code}</PlainCode>}
    </div>
  );
};

// Full-height panel pinned to the right edge, over the page header. Visibility is
// gated by the parent (only while editing and there are codes to reassign).
export const UnassignedCodesRail = ({ codes }) => (
  <RailPanel>
    <RailList>
      <RailHeading>Unassigned Group Codes:</RailHeading>
      {codes.map((code) => <DraggableCode key={code} code={code} />)}
    </RailList>
  </RailPanel>
);
